// Utils
import { syntax, TYPE } from "@/Motivators/helpers/specialUnits/InventorySearch/constants";
import { reservedCorrections, syntaxCorrections, syntaxRules } from "./syntax";
import { escapeForRegEx } from "@tiptap/core";

const isBoolean = (value = "", booleans = []) => {
	if (!value) return false;
	let check = value?.toString().toLowerCase();
	return check === "true" || check === "false" || booleans.includes(check);
};

const isNumeric = (number) => {
	return !isNaN(number) && !isNaN(parseFloat(number));
};

const lexer = (expression, lexicalCharacters = []) => {
	lexicalCharacters.forEach(({ symbol }) => {
		if (expression.includes(symbol)) {
			const regex = new RegExp(`(?<symbol>\\${symbol})(?=([^"]*"[^"]*")*[^"]*$)`, "g");
			expression = expression.replaceAll(regex, " $<symbol> ");
		}
	});
	const regexSpaces = /\s+(?=([^"]*"[^"]*")*[^"]*$)/g;
	expression = expression.replace(regexSpaces, " ").trim();
	// Split on spaces that are not inside double quotes
	const tokens = expression.match(/(?:[^\s"]+|"[^"]*")+/g);
	return tokens ? tokens.map((token) => token.trim()) : [];
};

const normalizer = (tokens = [], syntax = new Map()) => {
	return tokens.map((token) => syntax.get(token?.toString().toLowerCase()) || token);
};

const getType = (value, types = [], booleans = []) => {
	let definedType = types.find((item) => item.symbol === value);
	if (definedType) return definedType.type;

	// Special boolean check
	if (isBoolean(value, booleans)) {
		return TYPE.BOOLEAN;
	}

	return isNumeric(value) ? "number" : typeof value;
};

const normalizeStringKeyword = (value = "", startsWith = false, endsWith = false) => {
	let normalizedValue = "";
	if (startsWith && endsWith) {
		normalizedValue = `contains("${value}")`;
	} else if (startsWith) {
		normalizedValue = `startsWith("${value}")`;
	} else if (endsWith) {
		normalizedValue = `endsWith("${value}")`;
	}
	return normalizedValue;
};

const getValueWithProperties = (text = "") => {
	const parts = text.split(/(^\*|\*$)/g).filter((part) => part?.length);
	if (parts.length === 1) {
		return { value: parts[0], startsWith: false, endsWith: false, normalizedValue: parts[0] };
	}

	const endsWith = parts.at(0) === "*";
	const startsWith = parts.length > 1 && parts.at(-1) === "*";

	const value = parts[Number(Boolean(endsWith))];

	const normalizedValue = normalizeStringKeyword(value, startsWith, endsWith);

	return {
		value: escapeForRegEx(value),
		startsWith: startsWith && !endsWith ? "^" : "",
		endsWith: endsWith && !startsWith ? "$" : "",
		normalizedValue,
	};
};

/**
 * @returns {boolean|string|number}
 */
const parseTypedValue = (value = "", type = TYPE.STRING) =>
	type === TYPE.BOOLEAN ? Boolean(value?.toLowerCase() === "true" || value?.toLowerCase() === '"true"') : value;

const cleanValue = (value = "", type = TYPE.STRING) => {
	let newValue = value.replace(/(^["']|["']$)/g, "").trim();
	if (type === TYPE.STRING) {
		newValue = newValue.replace(/""/g, '"');
	}
	return newValue;
};

const buildDefinitions = (tokens = [], types = []) => {
	const booleans = types.filter((item) => item.type === TYPE.BOOLEAN).map((item) => item.symbol);
	const excludedWildcardsTypes = [TYPE.BOOLEAN, TYPE.UNDEFINED, TYPE.SEPARATOR];
	const mandatoryRule = /^".*"$/;

	return tokens?.map((token) => {
		let type = getType(token, types, booleans);
		let value = parseTypedValue(token, type);
		let normalizedValue;
		if ([TYPE.IDENTIFIER, TYPE.STRING].includes(type)) {
			// Check for mandatory compliance with the syntax
			if (!mandatoryRule.test(value)) {
				return {
					type: TYPE.UNDEFINED,
					value,
				};
			}
			value = cleanValue(value, type);
		}

		const isExcludedType = excludedWildcardsTypes.some((item) => item === type);

		if (!/(^\*|\*$)/.test(value) && type === TYPE.STRING) {
			value = escapeForRegEx(value);
		} else if (value && !isExcludedType && /(^\*|\*$)/.test(value)) {
			const { value: parsedValue, startsWith, endsWith, normalizedValue: updatedNormalizedValue } = getValueWithProperties(value);
			if (["*", "**"].includes(value?.trim())) {
				return { type: TYPE.UNDEFINED, value };
			}
			value = `/${startsWith}${parsedValue}${endsWith}/`;
			normalizedValue = updatedNormalizedValue;
		}

		return {
			type,
			value,
			normalizedValue,
		};
	});
};

const contextualize = (tokens = []) => {
	let result = [...tokens];
	let addedExpressions = 0;
	let needsToCloseGroup = false;

	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i];
		const nextToken = tokens?.[i + 1];
		const previousToken = tokens?.[i - 1];

		switch (token.type) {
			case TYPE.ASSIGNER:
				if (nextToken?.type === TYPE.OPERATOR) {
					addedExpressions--;
					result.splice(i + addedExpressions + 1, 1);
				}
				break;
			case TYPE.SEPARATOR:
				if (["]", ")"].includes(token.value) && ["[", "("].includes(nextToken?.value)) {
					addedExpressions++;
					result.splice(i + addedExpressions, 0, { type: TYPE.LOGICAL, value: "OR" });
				}

				if (needsToCloseGroup && token.value === "]") {
					needsToCloseGroup = false;
					addedExpressions++;
					result.splice(i + addedExpressions, 0, { type: TYPE.SEPARATOR, value: ")" });
				}
				break;
			case TYPE.STRING:
			case TYPE.NUMBER:
				if (nextToken?.type === token?.type) {
					addedExpressions++;
					result.splice(i + addedExpressions, 0, { type: TYPE.LOGICAL, value: "OR" });
				}
				break;
			case TYPE.LOGICAL:
				if (["]", ")"].includes(previousToken?.value) && token.value === "NOT") {
					addedExpressions += 2;
					needsToCloseGroup = true;
					result.splice(i + addedExpressions - 2, 0, { type: TYPE.OPERATOR, value: "OR" });
					result.splice(i + addedExpressions - 1, 0, { type: TYPE.SEPARATOR, value: "(" });
					break;
				}

				if ((previousToken?.type !== TYPE.SEPARATOR || previousToken?.value !== "(") && token.value === "NOT") {
					addedExpressions++;
					needsToCloseGroup = true;
					result.splice(i + addedExpressions - 1, 0, { type: TYPE.SEPARATOR, value: "(" });
				}
				break;
			default:
				break;
		}
	}
	return result;
};

const countLogical = (tokens = []) => {
	let count = 0;
	for (const token of tokens) {
		if (token?.type === TYPE.LOGICAL && token.value !== "NOT") {
			count++;
		}
	}
	return count;
};

const addParenthesis = (expression = []) => {
	let amount = countLogical(expression);
	if (amount) {
		expression.unshift({ type: TYPE.SEPARATOR, value: "(" });
		expression.push({ type: TYPE.SEPARATOR, value: ")" });
	}
	return expression;
};

const getTokenIdentifier = (expression = [], assigner = ":") => {
	let index = expression.findIndex((item) => item.value === assigner);
	return expression?.[index - 1];
};

const splitLogical = (expression = [], assigner = ":") => {
	let amount = countLogical(expression);
	let identifier = getTokenIdentifier(expression, assigner);
	if (amount && identifier) {
		let produced = [];

		for (let i = 0; i < expression.length; i++) {
			const token = expression[i];
			if (token?.type === TYPE.IDENTIFIER || (token?.type === TYPE.ASSIGNER && token?.value === assigner)) continue;

			if (token?.type === TYPE.LOGICAL) {
				produced.push({ type: TYPE.SEPARATOR, value: "]" }, token, { type: TYPE.SEPARATOR, value: "[" });
			} else {
				if (token.type === TYPE.SEPARATOR) {
					produced.push(token);
				} else {
					produced.push(identifier, { type: TYPE.ASSIGNER, value: assigner }, token);
				}
			}
		}
		return produced;
	}
	return expression;
};

function traverseExpression(tokens = [], assigner = ":") {
	let expression = [];
	let end = 0;

	for (let i = 0; i < tokens.length; i++) {
		let token = tokens[i];
		if (token.type === TYPE.SEPARATOR && token.value === "[") {
			let subExpression = [];
			end = i;
			while (token && token.value !== "]") {
				token = tokens[end];
				subExpression.push(token);
				end++;
			}

			if (subExpression.length) {
				subExpression = addParenthesis(subExpression);
				subExpression = splitLogical(subExpression, assigner);
				expression = [...expression, ...subExpression];
			}
			i = end - 1;
		} else {
			expression.push(token);
		}
	}
	return expression;
}

const parseContextTokens = (tokens = [], assigner = ":") => {
	return traverseExpression(tokens, assigner);
};

/**
 *
 * @param {string} expression
 * @param {*} lexicalCharacters
 * @returns {{
 * 		type: string,
 * 		value: string
 * }[]}
 */
const tokenize = (expression, lexicalCharacters = []) => {
	// Fix the language reserved words
	expression = reservedCorrections(expression);

	// Correct the expression following the syntax
	expression = syntaxCorrections(expression, syntax);

	// Tokenize the expression
	let tokens = lexer(expression, lexicalCharacters);

	// Apply basic syntax rules to the tokens
	tokens = syntaxRules(tokens);

	// Normalize the tokens following the syntax
	const normalizedTokens = normalizer(tokens, syntax);

	// Tokens definitions
	const tokensDefinitions = buildDefinitions(normalizedTokens, lexicalCharacters);

	// Correct the tokens definitions
	const addContext = contextualize(tokensDefinitions);

	return parseContextTokens(addContext);
};

export { tokenize };
